import { useCallback, useState } from 'react'

import { ENTITY_TYPES } from '@sales-center/common'
import { useAppDispatch } from '@user/store/hooks'
import { getLeadValue } from '@user/store/lead'
import { getOpportunityValue } from '@user/store/opportunity'
import type { RequirementsToCloseProps } from '../typings'
import { useClosingReqContext } from '../context'
import { setActiveRequirementActionCreator } from '../state'
import { clearAgreementConfigs, IForm } from '@user/store/agreements'

export function useHandlers(props: RequirementsToCloseProps) {
  const { entityId, entityType } = props

  const appDispatch = useAppDispatch()

  const [svcState, setSvcState] = useState<{
    svcOpen: boolean
    isAddingService: boolean
    openedFromAgreement: boolean
  }>(() => ({
    svcOpen: false,
    isAddingService: false,
    openedFromAgreement: false,
  }))

  const [, dispatch] = useClosingReqContext()
  const storeDispatch = useAppDispatch()
  const [createDrawerOpen, setCreateDrawerOpen] = useState<boolean>(false)

  const onCloseSvc = useCallback(() => {
    if (entityType === ENTITY_TYPES.Lead) appDispatch(getLeadValue(entityId))
    else appDispatch(getOpportunityValue(entityId))

    dispatch(setActiveRequirementActionCreator(undefined))

    setSvcState({
      svcOpen: false,
      isAddingService: false,
      openedFromAgreement: !!svcState.openedFromAgreement,
    })
  }, [
    appDispatch,
    dispatch,
    entityId,
    entityType,
    svcState.openedFromAgreement,
  ])

  const onOpenAndAddSvc = useCallback(() => {
    setSvcState({
      svcOpen: true,
      isAddingService: true,
      openedFromAgreement: false,
    })
  }, [])

  const onOpenSvc = useCallback(() => {
    setSvcState({
      svcOpen: true,
      isAddingService: false,
      openedFromAgreement: false,
    })
  }, [])

  const onOpenSvcFromAgreement = useCallback(() => {
    setSvcState({
      svcOpen: true,
      isAddingService: false,
      openedFromAgreement: true,
    })
  }, [])

  const openCreateDrawer = useCallback(
    (formData?: IForm) => {
      if (!formData) {
        storeDispatch(clearAgreementConfigs())
      }
      setCreateDrawerOpen(true)
      dispatch(setActiveRequirementActionCreator('agreement'))
    },
    [dispatch, storeDispatch],
  )

  return {
    ...svcState,
    onCloseSvc,
    onOpenAndAddSvc,
    onOpenSvc,
    onOpenSvcFromAgreement,
    openCreateDrawer,
    createDrawerOpen,
    setCreateDrawerOpen,
  }
}
